import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { OtpInput } from '../components/OtpInput';
import '../styles/trackApplication.scss';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const OTP_LENGTH = 4;
const RESEND_SECONDS = 60;

const STATUS_LABELS = {
  pending: 'قيد المراجعة',
  accepted: 'مقبول',
  rejected: 'مرفوض'
};

const FALLBACK_CITIES = [
  'طرابلس', 'بنغازي', 'مصراته', 'الزاوية', 'الخمس', 'زليتن',
  'سبها', 'البيضاء', 'درنة', 'طبرق', 'غريان', 'صبراته'
];

export const TrackApplication = () => {
  const { referenceNumber } = useParams();
  const [application, setApplication] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // step: 'status' | 'otp' | 'edit'
  const [step, setStep] = useState('status');
  const [otp, setOtp] = useState('');
  const [otpStatus, setOtpStatus] = useState('idle'); // idle | sending | sent
  const [resendTimer, setResendTimer] = useState(0);
  const [otpError, setOtpError] = useState('');

  const [cities, setCities] = useState(FALLBACK_CITIES);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [formData, setFormData] = useState(null);
  const [originalNationalNumber, setOriginalNationalNumber] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const timerRef = useRef(null);

  const fetchApplication = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/website/applications/${referenceNumber}`);
      const json = await res.json();
      if (!res.ok) {
        setError(json.message || 'لم يتم العثور على طلب بهذا الرقم المرجعي');
      } else {
        setApplication(json);
      }
    } catch (err) {
      setError('تعذر الاتصال بالخادم');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceNumber]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/website/content`)
      .then((res) => res.ok && res.json())
      .then((data) => {
        if (data && Array.isArray(data.requiredDocuments)) {
          setRequiredDocuments(data.requiredDocuments);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) {
      clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [resendTimer]);

  const isResubmission = application?.status === 'rejected';
  const canEdit = application && (
    application.status === 'pending' ||
    (application.status === 'rejected' && application.editedTimes === 0)
  );

  const startEditFlow = async () => {
    setOtpError('');
    setOtpStatus('sending');
    setStep('otp');
    try {
      const res = await fetch(`${API_BASE_URL}/api/website/applications/${referenceNumber}/request-edit-otp`, {
        method: 'POST'
      });
      const json = await res.json();
      if (!res.ok) {
        setOtpError(json.message);
        setOtpStatus('idle');
        return;
      }
      setOtpStatus('sent');
      setResendTimer(RESEND_SECONDS);
    } catch (err) {
      setOtpError('تعذر الاتصال بالخادم');
      setOtpStatus('idle');
    }
  };

  const handleVerifyOtp = async () => {
    setOtpError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/website/applications/${referenceNumber}/verify-edit-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otpCode: otp })
      });
      const json = await res.json();
      if (!res.ok) {
        setOtpError(json.message);
        return;
      }

      setFormData({
        fullName: json.fullName || '',
        nationalNumber: json.nationalNumber || '',
        phoneNumber: json.phoneNumber || '',
        city: json.city || '',
        birthDate: json.birthDate ? json.birthDate.slice(0, 10) : ''
      });
      setOriginalNationalNumber(json.nationalNumber || '');

      fetch(`${API_BASE_URL}/api/website/content`)
        .then((res) => res.ok && res.json())
        .then((data) => {
          if (data && Array.isArray(data.cities) && data.cities.length > 0) {
            setCities(data.cities);
          }
        })
        .catch(() => {});

      setStep('edit');
    } catch (err) {
      setOtpError('تعذر الاتصال بالخادم');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.fullName.trim()) errors.fullName = 'الاسم الكامل مطلوب';
    if (!formData.nationalNumber.trim()) errors.nationalNumber = 'الرقم الوطني مطلوب';
    if (!formData.phoneNumber.trim()) errors.phoneNumber = 'رقم الهاتف مطلوب';
    else if (!/^09\d{8}$/.test(formData.phoneNumber.trim())) errors.phoneNumber = 'رقم الهاتف يجب أن يتكون من 10 أرقام ويبدأ بـ 09';
    if (!formData.city) errors.city = 'يرجى اختيار المدينة';
    if (!formData.birthDate) errors.birthDate = 'تاريخ الميلاد مطلوب';
    if (isResubmission && formData.nationalNumber.trim() === originalNationalNumber) {
      errors.nationalNumber = 'يجب تغيير الرقم الوطني عند إعادة التقديم';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/website/applications/${referenceNumber}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otpCode: otp, ...formData })
      });
      const json = await res.json();
      if (!res.ok) {
        setFormErrors((prev) => ({ ...prev, submit: json.message }));
        setIsSaving(false);
        return;
      }

      setSaveMessage(isResubmission ? 'تم إعادة تقديم طلبك بنجاح' : 'تم تحديث بياناتك بنجاح');
      setOtp('');
      setStep('status');
      await fetchApplication();
    } catch (err) {
      setFormErrors((prev) => ({ ...prev, submit: 'تعذر الاتصال بالخادم' }));
    }
    setIsSaving(false);
  };

  return (
    <div className="track-page">
      <div className="track-container">
        {isLoading ? (
          <div className="loading">جارٍ التحميل...</div>
        ) : error ? (
          <div className="error-state">
            <div className="error-icon">✕</div>
            <h1>غير موجود</h1>
            <p>{error}</p>
          </div>
        ) : step === 'status' ? (
          <>
            <div className={`status-icon ${application.status}`}>
              {application.status === 'rejected' ? '✕' : '✓'}
            </div>
            <h1>معلومات الطلب</h1>

            {saveMessage && <p className="save-message">{saveMessage}</p>}

            <div className="info-list">
              <div className="info-row">
                <span className="label">الحالة</span>
                <span className={`value status-text ${application.status}`}>
                  {STATUS_LABELS[application.status] || application.status}
                </span>
              </div>
              <div className="info-row">
                <span className="label">الاسم الكامل</span>
                <span className="value">{application.fullName}</span>
              </div>
              <div className="info-row">
                <span className="label">الرقم المرجعي</span>
                <span className="value" dir="ltr">{application.referenceNumber}</span>
              </div>
              <div className="info-row">
                <span className="label">الرقم الوطني</span>
                <span className="value" dir="ltr">{application.nationalNumber}</span>
              </div>
              <div className="info-row">
                <span className="label">تاريخ الميلاد</span>
                <span className="value">
                  {new Date(application.birthDate).toLocaleDateString('ar-LY')}
                </span>
              </div>
              <div className="info-row">
                <span className="label">المدينة</span>
                <span className="value">{application.city}</span>
              </div>
              <div className="info-row">
                <span className="label">تاريخ التقديم</span>
                <span className="value">
                  {new Date(application.submittedAt).toLocaleDateString('ar-LY')}
                </span>
              </div>
            </div>

            {application.status === 'rejected' && (
              <div className="rejection-box">
                {application.rejectionReason && (
                  <p>سبب الرفض: {application.rejectionReason}</p>
                )}
                {application.editedTimes === 0 ? (
                  <p>يمكنك تعديل بياناتك وإعادة تقديم الطلب مرة واحدة فقط.</p>
                ) : (
                  <p>لقد استخدمت محاولة إعادة التقديم المسموح بها.</p>
                )}
              </div>
            )}

            {requiredDocuments.length > 0 && (
              <div className="documents-box">
                <h2>المستندات المطلوبة عند الحضور</h2>
                <ul>
                  {requiredDocuments.map((doc, index) => (
                    <li key={index}>{doc}</li>
                  ))}
                </ul>
              </div>
            )}

            {canEdit && (
              <button type="button" className="edit-btn" onClick={startEditFlow}>
                {isResubmission ? 'إعادة تقديم الطلب' : 'تعديل بيانات الطلب'}
              </button>
            )}
          </>
        ) : step === 'otp' ? (
          <>
            <h1>التحقق من الهوية</h1>
            <p className="hint-text">
              تم إرسال رمز تحقق إلى رقم الهاتف المسجل في الطلب. أدخله لتتمكن من تعديل بياناتك.
            </p>

            <div className="otp-section">
              <OtpInput length={OTP_LENGTH} value={otp} onChange={setOtp} disabled={otpStatus === 'sending'} />
              {otpError && <span className="error-message">{otpError}</span>}

              <div className="otp-resend">
                {resendTimer > 0 ? (
                  <span>يمكنك إعادة إرسال الرمز بعد {resendTimer} ثانية</span>
                ) : (
                  <button type="button" onClick={startEditFlow}>إعادة إرسال الرمز</button>
                )}
              </div>

              <button
                type="button"
                className="edit-btn"
                onClick={handleVerifyOtp}
                disabled={otp.length !== OTP_LENGTH}
              >
                تحقق
              </button>
              <button type="button" className="cancel-link" onClick={() => setStep('status')}>
                إلغاء
              </button>
            </div>
          </>
        ) : (
          <form className="edit-form" onSubmit={handleSave}>
            <h1>{isResubmission ? 'إعادة تقديم الطلب' : 'تعديل بيانات الطلب'}</h1>

            {isResubmission && (
              <p className="hint-text">يجب تغيير الرقم الوطني عن القيمة السابقة لإعادة التقديم.</p>
            )}

            <div className={`input-group ${formErrors.fullName ? 'error' : ''}`}>
              <label htmlFor="fullName">الاسم الكامل</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleFormChange}
              />
              {formErrors.fullName && <span className="error-message">{formErrors.fullName}</span>}
            </div>

            <div className={`input-group ${formErrors.nationalNumber ? 'error' : ''}`}>
              <label htmlFor="nationalNumber">الرقم الوطني</label>
              <input
                type="text"
                id="nationalNumber"
                name="nationalNumber"
                inputMode="numeric"
                value={formData.nationalNumber}
                onChange={handleFormChange}
              />
              {formErrors.nationalNumber && <span className="error-message">{formErrors.nationalNumber}</span>}
            </div>

            <div className={`input-group ${formErrors.phoneNumber ? 'error' : ''}`}>
              <label htmlFor="phoneNumber">رقم الهاتف</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                dir="ltr"
                inputMode="numeric"
                maxLength={10}
                value={formData.phoneNumber}
                onChange={handleFormChange}
              />
              {formErrors.phoneNumber && <span className="error-message">{formErrors.phoneNumber}</span>}
            </div>

            <div className={`input-group ${formErrors.city ? 'error' : ''}`}>
              <label htmlFor="city">المدينة</label>
              <select id="city" name="city" value={formData.city} onChange={handleFormChange}>
                <option value="" disabled>اختر المدينة</option>
                {cities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              {formErrors.city && <span className="error-message">{formErrors.city}</span>}
            </div>

            <div className={`input-group ${formErrors.birthDate ? 'error' : ''}`}>
              <label htmlFor="birthDate">تاريخ الميلاد</label>
              <input
                type="date"
                id="birthDate"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleFormChange}
              />
              {formErrors.birthDate && <span className="error-message">{formErrors.birthDate}</span>}
            </div>

            {formErrors.submit && <span className="error-message submit-error">{formErrors.submit}</span>}

            <button type="submit" className="edit-btn" disabled={isSaving}>
              {isSaving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
            </button>
            <button type="button" className="cancel-link" onClick={() => setStep('status')} disabled={isSaving}>
              إلغاء
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default TrackApplication;
